import { useCallback, useRef, useState } from "react";
import { Upload } from "lucide-react";

/**
 * Apple-style Drag & Drop uploader.
 *
 * Props:
 *  - onFiles(files: File[]): void — called with the accepted dropped/picked files
 *  - accept: string — HTML accept attribute (default images + pdf)
 *  - multiple: boolean — default true
 *  - disabled: boolean
 *  - hint: string — small helper text
 *  - testid: string
 *  - compact: boolean — smaller variant
 *  - children: optional custom body (icon + main text)
 */
export default function DropZone({
  onFiles,
  accept = "image/*,application/pdf",
  multiple = true,
  disabled = false,
  hint = "PDF o imagen · Arrastra aquí o haz clic",
  testid = "dropzone",
  compact = false,
  children,
}) {
  const inputRef = useRef(null);
  const [hover, setHover] = useState(false);

  const handleFiles = useCallback((fileList) => {
    if (!fileList || !fileList.length) return;
    const files = Array.from(fileList);
    onFiles && onFiles(multiple ? files : [files[0]]);
  }, [onFiles, multiple]);

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setHover(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setHover(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setHover(false);
    if (disabled) return;
    handleFiles(e.dataTransfer?.files);
  };

  return (
    <button
      type="button"
      className={`dropzone${hover ? " hover" : ""}${compact ? " compact" : ""}${disabled ? " disabled" : ""}`}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragEnter={onDragOver}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={testid}
      disabled={disabled}
      aria-label="Arrastra archivos aquí o haz clic para elegir"
    >
      {children || (
        <>
          <div className="dropzone-icon"><Upload size={compact ? 18 : 26} /></div>
          <b>{hover ? "Suelta para subir" : "Arrastra tu archivo aquí"}</b>
          <small>{hint}</small>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
    </button>
  );
}
